/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { sumBy } from 'lodash'

import type { AccessorFn, Key } from '@elastic/charts'

import { colorForInstances } from '@modules/billing-lib/colorGenerator'
import type { ChartItemType } from '@modules/billing-api/customTypes'

import { ProductType } from '@/components/Billing/BillingUsage/types'

import { getAvailableChartValueTypes } from '../../../BillingUsageOverviewV2/utils'
import {
  ColorKeyForOtherCCM,
  ColorKeyForOtherDeployments,
  ColorKeyForOtherProjects,
  VisibleInstances,
} from '../../../constants'

import type { YAccessorName } from './types'

export const prepareAggregatedInstanceDataAccessors = (productTypes: ProductType[]) => {
  const accessors: AccessorFn[] = []
  productTypes.map((productType) => {
    const accessor: AccessorFn = ({ values }) => {
      const items = values.filter(({ type }) => mapChartTypeToProductType(type) === productType)
      accessor.fieldName = productType
      return sumBy(items, 'value') || 0
    }

    accessors.push(accessor)
  })

  return accessors
}

export const getTopListOfInstances = (
  instanceRanking: Array<{ id: string; name?: string }>,
): { topInstances: string[]; hasMore: boolean } => ({
  topInstances: instanceRanking
    .slice(0, VisibleInstances)
    .map(({ id, name }) => constructKey(id, name)),
  hasMore: instanceRanking.length > VisibleInstances,
})

export const prepareInstanceDataAccessors = ({
  topInstances,
  hasMore,
  type,
}: {
  topInstances: string[]
  hasMore: boolean
  type?: ProductType
}) => {
  const accessors: AccessorFn[] = []

  if (!type) {
    return accessors
  }

  topInstances.map((instance) => {
    const accessor: AccessorFn = ({ values }) => {
      const item = values.find(({ id, name }) => constructKey(id, name) === instance)

      // fieldName must be unique thats we we cannot rely only on name and instance is build based on an id_name pair
      accessor.fieldName = instance

      return item?.value || 0
    }

    accessors.push(accessor)
  })

  // we need to add one more accessor `Other` which is a sum of the rest
  if (hasMore) {
    const accessor: AccessorFn = ({ values }) => {
      // filter out top instances
      const items = values.filter(({ id, name }) => !topInstances.includes(constructKey(id, name)))

      accessor.fieldName = {
        [ProductType.Deployments]: ColorKeyForOtherDeployments,
        [ProductType.Projects]: ColorKeyForOtherProjects,
        [ProductType.CCM]: ColorKeyForOtherCCM,
      }[type]

      return sumBy(items, 'value') || 0
    }

    accessors.push(accessor)
  }

  return accessors
}

export const getNameFromYAccessor = (yAccessor: YAccessorName, isAggregated: boolean) => {
  if (isAggregated) {
    // check if we display charts for both deployments and projects
    return mapProductTypeToChartLabel(yAccessor as ProductType)
  }

  // it removes ID from string and returns the rest as a name
  // example: a81554de1fca40cfb601278b5ee44d0c_keep_serverless-qa-obs-ux-mgmt -> keep_serverless-qa-obs-ux-mgmt
  return yAccessor.split(/_(.*)/)[1]
}

export const getColorFromYAccessor = (yAccessor: YAccessorName, isAggregated: boolean): string => {
  if (isAggregated) {
    return mapProductTypeToColor(yAccessor as ProductType)
  }

  const id = yAccessor.split('_')[0] || ''

  return colorForInstances.get(id)
}

const constructKey = (id: string, name?: string): Key => (name ? `${id}_${name}` : `${id}_${id}`)

export const getFilteredData = ({ data, productTypes, solutions }) => {
  const availableValueTypes = getAvailableChartValueTypes(productTypes, solutions)

  return data.map(({ values, ...rest }) => ({
    ...rest,
    values: values.filter(({ type }) => availableValueTypes.includes(type as ChartItemType)),
  }))
}

const mapChartTypeToProductType = (chartType: ChartItemType): ProductType =>
  ({
    deployment: ProductType.Deployments,
    security: ProductType.Projects,
    elasticsearch: ProductType.Projects,
    observability: ProductType.Projects,
    cloud_connect: ProductType.CCM,
  })[chartType]

const mapProductTypeToChartLabel = (productType: ProductType) =>
  ({
    [ProductType.Deployments]: 'Hosted deployments',
    [ProductType.Projects]: 'Serverless projects',
    [ProductType.CCM]: 'Connected clusters',
  })[productType]

const mapProductTypeToColor = (productType: ProductType) =>
  ({
    [ProductType.Deployments]: colorForInstances.get(ColorKeyForOtherDeployments),
    [ProductType.Projects]: colorForInstances.get(ColorKeyForOtherProjects),
    [ProductType.CCM]: colorForInstances.get(ColorKeyForOtherCCM),
  })[productType]
